import React, { useEffect, useState } from 'react';
import ContactForm from '../ContactForm';
import MapSection from '../MapSection';
import PageHero from '../PageHero';
import {
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Clock,
  ExternalLink,
  Sparkles,
  ArrowUpRight,
  ShieldCheck,
  AlertCircle,
  ChevronDown,
  Building2,
  Navigation,
  Calendar,
  CheckCircle2,
  Award,
  Copy,
  Check,
  Stethoscope
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { branches } from '../../data/branches';
import { doctors } from '../../data/doctors';
import { useLanguage } from '../../context/LanguageContext';
import tekkeSangliImg from '../../assets/tekke-sangli.jpeg';

const Contact = () => {
  const { t, language } = useLanguage();
  const [activeBranchSlug, setActiveBranchSlug] = useState('sangli');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [copiedAddress, setCopiedAddress] = useState(false);

  const handleCopyAddress = (text) => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Main Sangli branch data definition for the interactive hub
  const sangliMainBranch = {
    slug: 'sangli',
    name: language === 'mr' ? 'श्री टेकके आय क्लिनिक, सांगली (मुख्य रुग्णालय)' : (language === 'kn' ? 'ಶ್ರೀ ಟೆಕ್ಕೆ ಐ ಕ್ಲಿನಿಕ್, ಸಾಂಗ್ಲಿ (ಮುಖ್ಯ ಆಸ್ಪತ್ರೆ)' : 'Sree Tekke Eye Clinic, Sangli (Main Hospital)'),
    title: 'SANGLI',
    city: 'Sangli',
    area: 'Central Bus Stand Road',
    state: 'Maharashtra',
    stateCode: 'MH',
    address: language === 'mr'
      ? 'श्री टेकके नेत्र रुग्णालय, मुख्य बस स्टँड रोड, सांगली, महाराष्ट्र ४१६४१६'
      : 'Sree Tekke Eye Clinic, Near Central Bus Stand, Sangli, Maharashtra 416416',
    phone: '+91 98765 43210',
    phones: ['+91 98765 43210', '+91 99232 42655'],
    whatsapp: '+91 98765 43210',
    timings: language === 'mr' ? 'सोम-शनि: सकाळी ९:०० - रात्री ८:०० | रवि: बंद (तातडीची सेवा उपलब्ध)' : 'Mon-Sat: 9:00 AM - 8:00 PM | Sun: Closed (Emergency Available)',
    mapUrl: 'https://maps.app.goo.gl/fnjzcYXjEd8KLXGh8',
    doctors: ['dr-ramakant-tekke', 'dr-vishakha-tekke'],
    image: tekkeSangliImg,
    facilities: language === 'mr'
      ? ['अत्याधुनिक लेझर ऑपरेशन थिएटर', 'टाकेविरहित मोतीबिंदू शस्त्रक्रिया', 'संगणकीय दृष्टितपासणी', '२४/७ आपत्कालीन कक्ष', 'प्रीमियम ऑप्टिकल स्टोअर', 'कॅशलेस मेडिक्लेम']
      : ['Advanced Laser OT', 'Stitchless Cataract Surgery', 'Computerized Vision Care', '24/7 Emergency Care', 'Premium Optical Store', 'Cashless Mediclaim']
  };

  const allHubBranches = [
    sangliMainBranch,
    ...branches.map(b => ({
      ...b,
      stateCode: b.slug === 'athani' ? 'KA' : 'MH',
      state: b.slug === 'athani' ? 'Karnataka' : 'Maharashtra',
      // Localized name/address if available in branchList translations
      name: (language === 'mr' && t(`branchList.${b.slug}.name`)) || b.name,
      address: (language === 'mr' && t(`branchList.${b.slug}.address`)) || b.address,
      facilities: (language === 'mr' && t(`branchList.${b.slug}.facilities`)) || b.facilities
    }))
  ];

  const filteredBranches = allHubBranches.filter(b => {
    if (selectedRegion === 'MH') return b.stateCode === 'MH';
    if (selectedRegion === 'KA') return b.stateCode === 'KA';
    return true;
  });

  const activeBranch = allHubBranches.find(b => b.slug === activeBranchSlug) || sangliMainBranch;

  // Localized FAQ data
  const faqs = [
    {
      q: language === 'mr'
        ? 'ओपीडी तपासणीसाठी आधी अपॉइंटमेंट घेणे आवश्यक आहे का?'
        : (language === 'kn' ? 'OPD ತಪಾಸಣೆಗೆ ಮುಂಚಿತವಾಗಿ ಅಪಾಯಿಂಟ್‌ಮೆಂಟ್ ಅಗತ್ಯವೇ?' : 'Do I need a prior appointment for general OPD checkup?'),
      a: language === 'mr'
        ? 'नाही, आपण थेट आमच्या कोणत्याही शाखेत ओपीडी वेळेत (सकाळी ९:०० ते रात्री ८:००) भेट देऊ शकता. तरीही वेटिंग टाळण्यासाठी व वेळेच्या सोयीसाठी ऑनलाईन अपॉइंटमेंट बुक करणे अधिक फायदेशीर ठरते.'
        : (language === 'kn'
          ? 'ಇಲ್ಲ, ನೀವು ನಮ್ಮ ಯಾವುದೇ ಶಾಖೆಗೆ OPD ಸಮಯದಲ್ಲಿ ಭೇಟಿ ನೀಡಬಹುದು. ಆದರೆ ಸಮಯ ಉಳಿಸಲು ಆನ್‌ಲೈನ್ ಅಪಾಯಿಂಟ್‌ಮೆಂಟ್ ಬುಕ್ ಮಾಡುವುದು ಸೂಕ್ತ.'
          : 'Walk-ins are always welcome during regular OPD hours (9:00 AM - 8:00 PM). However, booking an appointment online in advance ensures zero waiting time.'
        )
    },
    {
      q: language === 'mr'
        ? 'डोळ्याला तातडीची दुखापत किंवा इमर्जन्सी झाल्यास काय करावे?'
        : (language === 'kn' ? 'ತುರ್ತು ಕಣ್ಣಿನ ಸಮಸ್ಯೆ ಅಥವಾ ಗಾಯವಾದರೆ ಏನು ಮಾಡಬೇಕು?' : 'What should I do in case of an acute eye injury or emergency?'),
      a: language === 'mr'
        ? 'डोळ्याला दुखापत झाल्यास किंवा दृष्टी अचानक अंधुक झाल्यास आमच्या २४/७ आपत्कालीन हेल्पलाइन (+९१ ९८७६५ ४३२१०) वर तात्काळ संपर्क साधा. आमचे मुख्य रुग्णालय तातडीच्या उपचारांसाठी सज्ज आहे.'
        : (language === 'kn'
          ? 'ತುರ್ತು ಪರಿಸ್ಥಿತಿಯಲ್ಲಿ ನಮ್ಮ ೨೪/೭ ಹೆಲ್ಪ್‌ಲೈನ್ (+೯೧ ೯೮೭೬೫ ೪೩೨೧೦) ಗೆ ತಕ್ಷಣ ಕರೆ ಮಾಡಿ. ನಮ್ಮ ತಂಡ ತಕ್ಷಣ ಚಿಕಿತ್ಸೆ ನೀಡಲು ಸಿದ್ಧವಿದೆ.'
          : 'Please call our 24/7 priority emergency line immediately at +91 98765 43210. Our emergency ophthalmic care unit at the main hospital is equipped for immediate trauma management.'
        )
    },
    {
      q: language === 'mr'
        ? 'कॅशलेस मेडिक्लेम आणि आरोग्य विमा सुविधा उपलब्ध आहे का?'
        : (language === 'kn' ? 'ಕ್ಯಾಶ್‌ಲೆಸ್ ಮೆಡಿಕ್ಲೇಮ್ ಮತ್ತು ವಿಮೆ ಸೌಲಭ್ಯ ಲಭ್ಯವಿದೆಯೇ?' : 'Is cashless mediclaim and health insurance accepted?'),
      a: language === 'mr'
        ? 'होय, मोतीबिंदू, लेझर आणि डोळ्यांच्या सूक्ष्म शस्त्रक्रियांसाठी प्रमुख सरकारी व खाजगी आरोग्य विमा कंपन्या तसेच नामांकित TPA द्वारे कॅशलेस सुविधा उपलब्ध आहे.'
        : (language === 'kn'
          ? 'ಹೌದು, ಎಲ್ಲಾ ಪ್ರಮುಖ ವಿಮಾ ಕಂಪನಿಗಳು ಮತ್ತು TPA ಮೂಲಕ ಕ್ಯಾಶ್‌ಲೆಸ್ ಸೌಲಭ್ಯ ಲಭ್ಯವಿದೆ.'
          : 'Yes, we provide seamless cashless admission and reimbursement support for all major health insurance providers and government TPAs for eligible eye surgeries.'
        )
    },
    {
      q: language === 'mr'
        ? 'सांगली मुख्य रुग्णालयामध्ये इतर शहरांतून कसे पोहोचावे?'
        : (language === 'kn' ? 'ಸಾಂಗ್ಲಿ ಮುಖ್ಯ ಆಸ್ಪತ್ರೆಗೆ ಇತರ ನಗರಗಳಿಂದ ಹೇಗೆ ತಲುಪುವುದು?' : 'How do I reach the main hospital from other cities/bus stand?'),
      a: language === 'mr'
        ? 'सांगली मध्यवर्ती बस स्टँड आणि रेल्वे स्टेशनवरून क्लिनिक अवघ्या ५ ते १० मिनिटांच्या अंतरावर आहे. ऑटो आणि सिटी बसेस सहज उपलब्ध आहेत. गुगल मॅप्सवर थेट नेव्हिगेशन उपलब्ध आहे.'
        : (language === 'kn'
          ? 'ಸಾಂಗ್ಲಿ ಬಸ್ ನಿಲ್ದಾಣ ಮತ್ತು ರೈಲ್ವೆ ನಿಲ್ದಾಣದಿಂದ ಆಸ್ಪತ್ರೆ ೫-೧೦ ನಿಮಿಷಗಳ ಅಂತರದಲ್ಲಿದೆ.'
          : 'The main hospital is conveniently situated just 5-10 minutes from Sangli Central Bus Stand and Railway Station, with ample parking and direct road access.'
        )
    }
  ];

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      {/* 1. Attractive Hero Banner matching Branches.js */}
      <PageHero
        badge={t('contactSection.badge')}
        title={t('contactSection.title')}
        subtitle={t('contactSection.pageSubtitle')}
      >
        {/* Animated Hero Interactive Pills */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs sm:text-sm font-semibold backdrop-blur-md shadow-xs">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping"></span>
            <span>{language === 'mr' ? 'दैनिक ओपीडी: सकाळी ९ ते रात्री ८ सुरू' : 'Daily OPD: 9 AM - 8 PM Open Today'}</span>
          </div>

          <a
            href="tel:+919876543210"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white text-white hover:text-navy text-xs sm:text-sm font-semibold transition-all border border-white/20 backdrop-blur-md"
          >
            <Phone size={14} className="text-teal" />
            <span>+91 98765 43210</span>
          </a>

          <Link
            to="/appointment"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal text-navy hover:bg-white text-xs sm:text-sm font-bold transition-all shadow-md"
          >
            <Calendar size={14} />
            <span>{t('nav.bookAppointment')}</span>
          </Link>
        </div>
      </PageHero>

      {/* 2. Main Container (Positioned naturally below Hero without overlap) */}
      <div className="container mx-auto px-4 lg:px-8 pt-10 sm:pt-14 relative z-20 pb-24">

        {/* 4 Interactive Quick-Action Floating Cards - Golden Brand Theme */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">

          {/* Card 1: Emergency Hotline */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            whileHover={{ y: -6, transition: { duration: 0.2 } }}
            className="bg-gradient-to-b from-[#FFFDF5] via-amber-50/40 to-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-amber-500/15 border border-amber-200/90 hover:border-amber-400 relative overflow-hidden group transition-all"
          >
            {/* Top Accent Golden Bar */}
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-400"></div>

            {/* Ambient Corner & Bottom Golden Glow */}
            <div className="absolute top-0 right-0 w-28 h-28 bg-amber-400/10 rounded-bl-full pointer-events-none group-hover:scale-125 transition-transform duration-300"></div>
            <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-amber-400/10 rounded-full blur-xl pointer-events-none"></div>

            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-amber-400 group-hover:to-amber-600 group-hover:text-white transition-all duration-300 shadow-xs group-hover:shadow-md group-hover:shadow-amber-500/25">
                <Phone size={22} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-amber-50 border border-amber-300/80 text-amber-800 shadow-2xs">
                {language === 'mr' ? '२४/७ आपत्कालीन' : '24/7 Helpline'}
              </span>
            </div>

            <h4 className="text-base font-bold text-navy mb-1 relative z-10 group-hover:text-amber-950 transition-colors">
              {language === 'mr' ? 'आपत्कालीन हेल्पलाइन' : 'Emergency & Trauma'}
            </h4>
            <p className="text-gray-600 text-xs mb-4 relative z-10">
              {language === 'mr' ? 'डोळ्याला दुखापत किंवा तात्काळ मदत' : 'Immediate ophthalmic urgent assistance'}
            </p>

            <a
              href="tel:+919876543210"
              className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-800 text-xs font-bold group-hover:underline relative z-10"
            >
              <span>{t('contactSection.callNow')} (+91 98765 43210)</span>
              <ArrowUpRight size={14} className="text-amber-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </motion.div>

          {/* Card 2: Instant WhatsApp */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -6, transition: { duration: 0.2 } }}
            className="bg-gradient-to-b from-[#FFFDF5] via-amber-50/40 to-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-amber-500/15 border border-amber-200/90 hover:border-amber-400 relative overflow-hidden group transition-all"
          >
            {/* Top Accent Golden Bar */}
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-400"></div>

            {/* Ambient Corner & Bottom Golden Glow */}
            <div className="absolute top-0 right-0 w-28 h-28 bg-amber-400/10 rounded-bl-full pointer-events-none group-hover:scale-125 transition-transform duration-300"></div>
            <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-amber-400/10 rounded-full blur-xl pointer-events-none"></div>

            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-amber-400 group-hover:to-amber-600 group-hover:text-white transition-all duration-300 shadow-xs group-hover:shadow-md group-hover:shadow-amber-500/25">
                <MessageCircle size={22} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-amber-50 border border-amber-300/80 text-amber-800 shadow-2xs">
                {language === 'mr' ? '५ मिनिटात उत्तर' : 'Fast Chat'}
              </span>
            </div>

            <h4 className="text-base font-bold text-navy mb-1 relative z-10 group-hover:text-amber-950 transition-colors">
              {t('contactSection.whatsapp')}
            </h4>
            <p className="text-gray-600 text-xs mb-4 relative z-10">
              {language === 'mr' ? 'अपॉइंटमेंट व तपासणी शंका निरसन' : 'Quick consultation & reports query'}
            </p>

            <a
              href="https://wa.me/919876543210?text=Hello%20Dr.%20Tekke%20Eye%20Clinic,%20I%20would%20like%20to%20inquire%20about"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-800 text-xs font-bold group-hover:underline relative z-10"
            >
              <span>{t('contactSection.messageUs')}</span>
              <ArrowUpRight size={14} className="text-amber-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </motion.div>

          {/* Card 3: Main Clinic Location */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            whileHover={{ y: -6, transition: { duration: 0.2 } }}
            className="bg-gradient-to-b from-[#FFFDF5] via-amber-50/40 to-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-amber-500/15 border border-amber-200/90 hover:border-amber-400 relative overflow-hidden group transition-all"
          >
            {/* Top Accent Golden Bar */}
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-400"></div>

            {/* Ambient Corner & Bottom Golden Glow */}
            <div className="absolute top-0 right-0 w-28 h-28 bg-amber-400/10 rounded-bl-full pointer-events-none group-hover:scale-125 transition-transform duration-300"></div>
            <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-amber-400/10 rounded-full blur-xl pointer-events-none"></div>

            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-amber-400 group-hover:to-amber-600 group-hover:text-white transition-all duration-300 shadow-xs group-hover:shadow-md group-hover:shadow-amber-500/25">
                <MapPin size={22} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-amber-50 border border-amber-300/80 text-amber-800 shadow-2xs">
                {language === 'mr' ? 'सांगली मुख्यालय' : 'Main Center'}
              </span>
            </div>

            <h4 className="text-base font-bold text-navy mb-1 relative z-10 group-hover:text-amber-950 transition-colors">
              {t('contactSection.mainClinicTitle')}
            </h4>
            <p className="text-gray-600 text-xs mb-4 relative z-10">
              {t('contactSection.mainClinicLocation')}
            </p>

            <a
              href="https://maps.app.goo.gl/fnjzcYXjEd8KLXGh8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-800 text-xs font-bold group-hover:underline relative z-10"
            >
              <span>{t('contactSection.openInMaps')}</span>
              <ExternalLink size={13} className="text-amber-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </motion.div>

          {/* Card 4: Email & Second Opinion */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ y: -6, transition: { duration: 0.2 } }}
            className="bg-gradient-to-b from-[#FFFDF5] via-amber-50/40 to-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-amber-500/15 border border-amber-200/90 hover:border-amber-400 relative overflow-hidden group transition-all"
          >
            {/* Top Accent Golden Bar */}
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-400"></div>

            {/* Ambient Corner & Bottom Golden Glow */}
            <div className="absolute top-0 right-0 w-28 h-28 bg-amber-400/10 rounded-bl-full pointer-events-none group-hover:scale-125 transition-transform duration-300"></div>
            <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-amber-400/10 rounded-full blur-xl pointer-events-none"></div>

            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-amber-400 group-hover:to-amber-600 group-hover:text-white transition-all duration-300 shadow-xs group-hover:shadow-md group-hover:shadow-amber-500/25">
                <Mail size={22} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-amber-50 border border-amber-300/80 text-amber-800 shadow-2xs">
                {language === 'mr' ? 'ईमेल संपर्क' : 'Email Desk'}
              </span>
            </div>

            <h4 className="text-base font-bold text-navy mb-1 relative z-10 group-hover:text-amber-950 transition-colors">
              {t('contactSection.email')}
            </h4>
            <p className="text-gray-600 text-xs mb-4 truncate relative z-10">
              info@sreetekke.com
            </p>

            <a
              href="mailto:info@sreetekke.com"
              className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-800 text-xs font-bold group-hover:underline relative z-10"
            >
              <span>{t('contactSection.sendEmail')}</span>
              <ArrowUpRight size={14} className="text-amber-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </motion.div>

        </div>

        {/* Section Header: Direct Consultation & Hospital Desk */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-800 text-xs font-bold uppercase tracking-wider mb-3 shadow-2xs">
            <Sparkles size={14} className="text-amber-600" />
            <span>{language === 'mr' ? 'तपासणी व रुग्ण सेवा केंद्र' : 'Direct Patient Helpdesk & Consultation'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-navy tracking-tight">
            {language === 'mr' ? 'रुग्णालयास भेट द्या किंवा थेट सल्ला मिळवा' : 'Visit Our Hospital or Connect Online'}
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm mt-2.5 leading-relaxed">
            {language === 'mr'
              ? 'आमच्या सांगली मुख्य रुग्णालयाचे कामकाजाचे तास, तज्ज्ञ डॉक्टर्स आणि शाखा नेटवर्कची माहिती येथे मिळवा.'
              : 'Access live clinic timings, specialist doctors availability, and comprehensive eye care guidance for your visit.'
            }
          </p>
        </div>

        {/* 3. Main 2-Column Section: Left Info Cards & Right Animated Form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mb-20 items-start">

          {/* Left Column (5 Cols) */}
          <div className="lg:col-span-5 space-y-6">

            {/* Clinic Info & Working Schedule Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl p-6 sm:p-8 shadow-md hover:shadow-xl border border-slate-200/90 relative overflow-hidden transition-all duration-300 group"
            >
              {/* Top Accent Gradient Bar */}
              <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-teal via-clinical to-amber-400"></div>

              {/* Subtle ambient light */}
              <div className="absolute top-0 right-0 w-44 h-44 bg-clinical/5 rounded-bl-full pointer-events-none group-hover:scale-110 transition-transform duration-500"></div>

              {/* Card Header */}
              <div className="flex items-center justify-between pb-6 border-b border-gray-100 relative z-10">
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <ShieldCheck size={14} className="text-teal" />
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-teal">
                      {language === 'mr' ? 'NABH मानांकित रुग्णालय' : 'NABH Standard Hospital'}
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-navy">
                    {t('contactSection.mainClinic')}
                  </h3>
                </div>
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200/80 px-3 py-1.5 rounded-full shadow-2xs">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-xs font-bold text-emerald-700">
                    {language === 'mr' ? 'ओपीडी सुरू आहे' : 'Open Today'}
                  </span>
                </div>
              </div>

              {/* Timings & Address Info Rows */}
              <div className="py-6 space-y-4 relative z-10">
                {/* Hours Box */}
                <div className="flex items-start gap-3.5 p-3.5 rounded-2xl bg-slate-50/80 border border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-blue-100/80 text-clinical flex items-center justify-center shrink-0 shadow-2xs">
                    <Clock size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h5 className="font-bold text-navy text-sm">{t('contactSection.workingHours')}</h5>
                      <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md bg-clinical/10 text-clinical">
                        {language === 'mr' ? 'दैनिक ओपीडी' : 'Daily OPD'}
                      </span>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm font-semibold mt-1">
                      {t('contactSection.monSatHours')}
                    </p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {t('contactSection.sundayHours')} • <span className="text-amber-700 font-semibold">{language === 'mr' ? '२४/७ आपत्कालीन' : '24/7 Trauma'}</span>
                    </p>
                  </div>
                </div>

                {/* Address Box */}
                <div className="flex items-start gap-3.5 p-3.5 rounded-2xl bg-slate-50/80 border border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-teal/15 text-teal flex items-center justify-center shrink-0 shadow-2xs">
                    <MapPin size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h5 className="font-bold text-navy text-sm">{t('contactSection.address')}</h5>
                      <button
                        type="button"
                        onClick={() => handleCopyAddress(sangliMainBranch.address)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-teal hover:text-navy cursor-pointer transition-colors"
                        title="Copy address"
                      >
                        {copiedAddress ? (
                          <>
                            <Check size={12} className="text-emerald-600" />
                            <span className="text-emerald-600">{language === 'mr' ? 'कॉपी झाले!' : 'Copied!'}</span>
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            <span>{language === 'mr' ? 'पत्ता कॉपी करा' : 'Copy'}</span>
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-gray-600 text-xs sm:text-sm mt-1 leading-relaxed">
                      {sangliMainBranch.address}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                      <span>📍</span>
                      <span>{language === 'mr' ? 'सांगली मध्यवर्ती बस स्थानकाजवळ (५ मिनिटे)' : 'Near Central Bus Stand, Sangli (5 min walk)'}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Direct Doctor Care Line Banner - Luxury Executive Style */}
              <div className="bg-gradient-to-r from-navy via-[#10273f] to-[#173a5e] text-white p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-md border border-amber-400/20 relative overflow-hidden z-10">
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/5 rounded-bl-full pointer-events-none"></div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center text-amber-400 shrink-0">
                    <Award size={20} />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold flex items-center gap-1.5 text-white">
                      <span>{language === 'mr' ? 'तज्ज्ञ डॉक्टर्स थेट सल्ला कक्ष' : 'Senior Specialist Consultation'}</span>
                    </h5>
                    <p className="text-xs text-amber-200/90 font-medium mt-0.5">
                      {language === 'mr' ? 'डॉ. रमाकांत टेकके व डॉ. विशाखा टेकके' : 'Dr. Ramakant Tekke & Dr. Vishakha Tekke'}
                    </p>
                    <p className="text-[10px] text-gray-300">
                      {language === 'mr' ? '३०+ वर्षांचा नेत्रतज्ज्ञ अनुभव' : '30+ Years Ophthalmology Excellence'}
                    </p>
                  </div>
                </div>

                <a
                  href="tel:+919876543210"
                  className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-navy font-extrabold text-xs transition-all shrink-0 shadow-md hover:scale-105 active:scale-95 flex items-center gap-1.5"
                >
                  <Phone size={13} />
                  <span>{t('contactSection.callNow')}</span>
                </a>
              </div>
            </motion.div>

            {/* Regional Network Quick Navigator */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-3xl p-6 sm:p-7 shadow-md hover:shadow-xl border border-slate-200/90 relative overflow-hidden transition-all duration-300"
            >
              {/* Top Accent Gradient Bar */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-teal via-clinical to-navy"></div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-clinical/10 text-clinical flex items-center justify-center">
                    <Building2 size={18} />
                  </div>
                  <div>
                    <h4 className="font-extrabold text-navy text-sm sm:text-base">
                      {language === 'mr' ? 'आमच्या इतर ६ शाखा' : 'Our 6 Branch Network'}
                    </h4>
                    <span className="text-[10px] text-teal font-bold uppercase tracking-wider">
                      {language === 'mr' ? 'महाराष्ट्र व कर्नाटक' : 'Maharashtra & Karnataka'}
                    </span>
                  </div>
                </div>
                <Link
                  to="/branches"
                  className="text-xs font-bold text-clinical hover:text-navy transition-colors flex items-center gap-1 group"
                >
                  <span>{t('contactSection.exploreAllBranches')}</span>
                  <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>

              <p className="text-gray-500 text-xs leading-relaxed mb-4">
                {language === 'mr'
                  ? 'आपल्या जवळच्या शाखेत तपासणी व मार्गदर्शनासाठी खालील केंद्रांवर थेट संपर्क करा:'
                  : 'Convenient eye clinics across Maharashtra & Karnataka for OPD, medicine, and evaluation:'
                }
              </p>

              {/* Enhanced 2-column or 3-column Branch Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {branches.map(b => (
                  <Link
                    key={b.slug}
                    to={`/branches/${b.slug}`}
                    className="flex flex-col justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-gradient-to-br hover:from-blue-50 hover:to-white border border-slate-200/80 hover:border-clinical/40 text-navy text-xs font-bold transition-all group shadow-2xs hover:shadow-sm"
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <span className="text-slate-400 group-hover:text-clinical transition-colors">
                        <MapPin size={12} />
                      </span>
                      <ArrowUpRight size={12} className="opacity-40 group-hover:opacity-100 group-hover:text-clinical group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                    </div>
                    <span className="text-navy group-hover:text-clinical truncate">{b.city || b.area}</span>
                  </Link>
                ))}
              </div>
            </motion.div>

          </div>

          {/* Right Column: Ultra-Attractive Creative Contact Form (7 Cols) */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>

        </div>

        {/* 4. Interactive Multi-Branch Hospital Location Hub */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-slate-200/90 mb-20 relative overflow-hidden"
        >
          {/* Top Brand Gradient Accent Bar */}
          <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-teal via-clinical to-amber-400"></div>

          {/* Ambient Decorative Blurs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-teal/10 via-clinical/5 to-transparent rounded-bl-full pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-amber-400/10 via-teal/5 to-transparent rounded-tr-full pointer-events-none"></div>

          {/* Hub Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-8 gap-5 relative z-10">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-xs font-extrabold uppercase tracking-widest shadow-2xs">
                  <MapPin size={13} />
                  <span>{language === 'mr' ? '७ सुसज्ज केंद्रे • २ राज्ये' : '7 Centers • 2 States'}</span>
                </span>
                <span className="text-xs text-amber-800 font-bold bg-amber-50 px-3 py-1 rounded-full border border-amber-200/90 flex items-center gap-1.5">
                  <Award size={13} className="text-amber-600" />
                  <span>{language === 'mr' ? 'महाराष्ट्र व कर्नाटक सुपर स्पेशालिटी नेटवर्क' : 'Maharashtra & Karnataka Care Network'}</span>
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-navy tracking-tight">
                {language === 'mr' ? 'परस्परसंवादी शाखा व स्थान केंद्र (Interactive Location Hub)' : t('branchesSection.findOnMap')}
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm mt-2 max-w-2xl leading-relaxed">
                {language === 'mr'
                  ? 'खालील राज्य किंवा शहर निवडा आणि थेट त्या शाखेचा सविस्तर पत्ता, उपलब्ध तज्ज्ञ डॉक्टर्स, ओपीडी वेळ, थेट संपर्क व गुगल नेव्हिगेशन मिळवा.'
                  : 'Select any region or city below to explore live clinic timings, consulting eye surgeons, direct phone lines, and interactive Google Maps directions.'
                }
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <a
                href={activeBranch.mapUrl || "https://maps.app.goo.gl/fnjzcYXjEd8KLXGh8"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-navy to-clinical hover:from-clinical hover:to-navy text-white text-xs sm:text-sm font-bold transition-all shadow-md hover:shadow-lg group"
              >
                <Navigation size={15} className="text-amber-400 group-hover:rotate-45 transition-transform duration-300" />
                <span>{language === 'mr' ? 'गुगल मॅप्सवर थेट मार्ग पहा' : 'Open Live Driving Route'}</span>
                <ExternalLink size={13} className="text-teal" />
              </a>
            </div>
          </div>

          {/* Regional Filter Pills (All / Maharashtra / Karnataka) */}
          <div className="flex items-center gap-2 mb-4 relative z-10">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-1 hidden sm:inline-block">
              {language === 'mr' ? 'राज्य निवडा:' : 'Filter Region:'}
            </span>
            <button
              onClick={() => {
                setSelectedRegion('all');
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                selectedRegion === 'all'
                  ? 'bg-navy text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {language === 'mr' ? 'सर्व ७ केंद्रे' : 'All 7 Centers'}
            </button>
            <button
              onClick={() => {
                setSelectedRegion('MH');
                if (activeBranchSlug === 'athani') setActiveBranchSlug('sangli');
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                selectedRegion === 'MH'
                  ? 'bg-navy text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span>{language === 'mr' ? 'महाराष्ट्र (६ शाखा)' : 'Maharashtra (6)'}</span>
            </button>
            <button
              onClick={() => {
                setSelectedRegion('KA');
                setActiveBranchSlug('athani');
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                selectedRegion === 'KA'
                  ? 'bg-navy text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span>{language === 'mr' ? 'कर्नाटक (१ शाखा)' : 'Karnataka (1)'}</span>
            </button>
          </div>

          {/* Interactive Branch Selector Horizontal Ribbon */}
          <div className="flex items-center gap-2.5 overflow-x-auto pb-4 mb-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden relative z-10">
            {filteredBranches.map((b) => {
              const isActive = activeBranch.slug === b.slug;
              return (
                <button
                  key={b.slug}
                  onClick={() => setActiveBranchSlug(b.slug)}
                  className={`px-4 py-3 rounded-2xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all duration-300 shrink-0 cursor-pointer flex items-center gap-2.5 border ${
                    isActive
                      ? 'bg-gradient-to-r from-navy via-clinical to-navy text-white border-clinical shadow-lg shadow-clinical/25 scale-[1.02] ring-2 ring-amber-400/50'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-navy border-slate-200/90'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-amber-400 animate-ping' : 'bg-slate-300'}`}></span>
                  <span>{b.title || b.city || b.name}</span>
                  {b.slug === 'sangli' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-extrabold uppercase ${
                      isActive ? 'bg-amber-400 text-navy' : 'bg-amber-100 text-amber-800'
                    }`}>
                      HQ
                    </span>
                  )}
                  {b.stateCode && b.slug !== 'sangli' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-extrabold uppercase ${
                      isActive ? 'bg-white/20 text-white' : 'bg-blue-100 text-clinical'
                    }`}>
                      {b.stateCode}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 12-Column Unified Interactive Studio: Left Details Dossier (5 Cols) + Right Dynamic Map (7 Cols) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch relative z-10">

            {/* Left Column (5 Cols): Selected Branch Dossier Card */}
            <div className="lg:col-span-5 flex flex-col justify-between bg-gradient-to-b from-[#FFFDF8] via-white to-slate-50 rounded-2xl border border-amber-200/80 shadow-md overflow-hidden">
              
              {/* Branch Facility Photo Banner with Floating Badges */}
              <div className="relative h-44 w-full overflow-hidden bg-slate-900 group/img">
                <img
                  src={activeBranch.image || 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?auto=format&fit=crop&q=80&w=800'}
                  alt={activeBranch.name}
                  className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/35 to-transparent"></div>

                {/* Floating Top Badges */}
                <div className="absolute top-3 inset-x-3 flex items-center justify-between gap-2 z-10">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-navy/80 backdrop-blur-md text-amber-300 text-xs font-bold border border-white/20 shadow-xs">
                    <MapPin size={12} className="text-teal" />
                    <span>{activeBranch.city || 'Center'}, {activeBranch.stateCode || 'MH'}</span>
                  </div>

                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/90 text-white text-xs font-bold backdrop-blur-md shadow-xs">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
                    <span>{language === 'mr' ? 'ओपीडी सुरू आहे' : 'Open Today'}</span>
                  </div>
                </div>

                {/* Floating Bottom Facility Badge on Photo */}
                <div className="absolute bottom-3 left-3 z-10">
                  {activeBranch.slug === 'sangli' ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-400 text-navy font-black text-[11px] uppercase tracking-wider shadow-md">
                      <Award size={13} />
                      <span>{language === 'mr' ? 'मुख्य सुपर-स्पेशालिटी रुग्णालय (HQ)' : 'Main Super-Speciality Hospital (HQ)'}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/90 backdrop-blur-md text-navy font-bold text-[11px] uppercase tracking-wider shadow-md border border-white/50">
                      <ShieldCheck size={13} className="text-teal" />
                      <span>{language === 'mr' ? 'अधिकृत सुसज्ज नेत्र केंद्र' : 'Authorized Eye Care Center'}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Dossier Card Content Body */}
              <div className="p-5 sm:p-6 space-y-4 flex-1 flex flex-col justify-between">
                <div>
                  {/* Hospital Branch Name */}
                  <h4 className="text-xl sm:text-2xl font-black text-navy leading-snug mb-3">
                    {activeBranch.name}
                  </h4>

                  {/* Consulting Specialists on Duty at this Branch */}
                  <div className="p-3.5 rounded-xl bg-amber-50/60 border border-amber-200/80 mb-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs font-extrabold text-amber-950 uppercase tracking-wider">
                        <Stethoscope size={14} className="text-amber-600" />
                        <span>{language === 'mr' ? 'या शाखेतील मार्गदर्शक तज्ज्ञ डॉक्टर:' : 'Consulting Eye Surgeons:'}</span>
                      </span>
                      <span className="text-[10px] font-bold text-amber-800 bg-amber-200/60 px-2 py-0.5 rounded-full">
                        {language === 'mr' ? 'नियमित सल्लामसलत' : 'Visiting OPD'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                      {(activeBranch.doctors || ['dr-ramakant-tekke', 'dr-vishakha-tekke']).map((docId) => {
                        const docObj = doctors.find(d => d.id === docId);
                        const docName = (language === 'mr' && t(`doctorsList.${docId}.name`)) || docObj?.name || (docId === 'dr-ramakant-tekke' ? 'Dr. Ramakant Tekke' : 'Dr. Vishakha Tekke');
                        const docSpec = (language === 'mr' && t(`doctorsList.${docId}.specialization`)) || docObj?.specialization || 'Eye Specialist';
                        return (
                          <div key={docId} className="flex items-center gap-2 p-1.5 rounded-lg bg-white border border-amber-100 shadow-2xs">
                            <img
                              src={docObj?.image || 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&q=80&w=400&h=400'}
                              alt={docName}
                              className="w-8 h-8 rounded-full object-cover object-top border border-amber-200 shrink-0"
                            />
                            <div className="min-w-0">
                              <span className="block text-xs font-bold text-navy truncate">{docName}</span>
                              <span className="block text-[10px] text-gray-500 truncate">{docSpec}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Address Box with Copy Action */}
                  <div className="p-3.5 rounded-xl bg-white border border-slate-200/80 mb-3 space-y-1">
                    <div className="flex items-center justify-between text-xs text-gray-500 font-semibold mb-1">
                      <span className="flex items-center gap-1 text-teal">
                        <MapPin size={13} />
                        <span>{t('contactSection.address')}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopyAddress(activeBranch.address)}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-teal hover:text-navy cursor-pointer transition-colors"
                        title="Copy address"
                      >
                        {copiedAddress ? (
                          <>
                            <Check size={12} className="text-emerald-600" />
                            <span className="text-emerald-600">{language === 'mr' ? 'कॉपी झाले!' : 'Copied!'}</span>
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            <span>{language === 'mr' ? 'कॉपी करा' : 'Copy'}</span>
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm leading-relaxed font-medium">
                      {activeBranch.address}
                    </p>
                  </div>

                  {/* Timings Box */}
                  {activeBranch.timings && (
                    <div className="p-3.5 rounded-xl bg-white border border-slate-200/80 mb-3 flex items-start gap-2.5">
                      <Clock size={16} className="text-clinical shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-bold text-navy block">
                          {language === 'mr' ? 'तपासणी व ओपीडी वेळ:' : 'Consultation Timings:'}
                        </span>
                        <p className="text-gray-600 text-xs mt-0.5 font-medium">
                          {activeBranch.timings}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Facilities Tags */}
                  {activeBranch.facilities && activeBranch.facilities.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block">
                        {language === 'mr' ? 'उपलब्ध सुविधा व सेवा:' : 'Key Facilities & Services:'}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {activeBranch.facilities.map((fac, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 bg-white px-2.5 py-1 rounded-lg border border-slate-200 shadow-2xs">
                            <CheckCircle2 size={12} className="text-teal" />
                            <span>{fac}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons Grid (Call, WhatsApp, and Book Appointment) */}
                <div className="pt-4 border-t border-slate-200/80 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <a
                      href={`tel:${activeBranch.phone.replace(/\s+/g, '')}`}
                      className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-navy hover:bg-clinical text-white font-bold text-xs transition-colors shadow-sm"
                    >
                      <Phone size={14} className="text-teal" />
                      <span>{t('contactSection.callNow')}</span>
                    </a>

                    <a
                      href={`https://wa.me/${(activeBranch.whatsapp || '+919876543210').replace(/[^0-9]/g, '')}?text=Hello%20Sree%20Tekke%20Eye%20Clinic,%20I%20am%20inquiring%20about%20${encodeURIComponent(activeBranch.city || 'clinic')}%20center`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition-colors shadow-sm"
                    >
                      <MessageCircle size={14} />
                      <span>{t('contactSection.whatsapp')}</span>
                    </a>
                  </div>

                  {/* Book Appointment at THIS branch CTA */}
                  <Link
                    to={`/appointment?branch=${activeBranch.slug}`}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-navy font-black text-xs transition-all shadow-sm hover:shadow-md"
                  >
                    <Calendar size={14} className="text-navy" />
                    <span>{language === 'mr' ? 'या शाखेत वेळ निश्चित करा (Book Appointment)' : 'Book Appointment at this Branch'}</span>
                    <ArrowUpRight size={13} />
                  </Link>
                </div>
              </div>

            </div>

            {/* Right Column (7 Cols): Dynamic Live Map Studio */}
            <div className="lg:col-span-7 rounded-2xl overflow-hidden border border-slate-200/90 relative min-h-[440px] lg:min-h-[520px] shadow-sm bg-slate-100 flex flex-col">
              <MapSection branch={activeBranch} height="h-full min-h-[440px] lg:min-h-[520px]" />
            </div>

          </div>
        </motion.div>

        {/* 5. Animated Frequently Asked Questions (FAQ) Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-10">
            <div className="flex items-center justify-center space-x-3 mb-2">
              <span className="w-8 h-[2px] bg-teal"></span>
              <span className="text-teal font-bold tracking-widest text-xs uppercase">
                {language === 'mr' ? 'वारंवार विचारले जाणारे प्रश्न' : 'FAQ & Patient Guidance'}
              </span>
              <span className="w-8 h-[2px] bg-teal"></span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-navy tracking-tight">
              {language === 'mr' ? 'आपल्या मनातील शंका व उत्तरे' : 'Have Questions Before You Visit?'}
            </h3>
            <p className="text-gray-500 text-sm mt-1 max-w-xl mx-auto">
              {language === 'mr'
                ? 'रुग्णालयात येण्यापूर्वी रुग्णांना पडणाऱ्या महत्त्वाच्या प्रश्नांची उत्तरे येथे दिली आहेत.'
                : 'Clear, helpful guidance for your smooth hospital visit and eye care appointments.'
              }
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs hover:border-gray-300 transition-colors"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                    className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 cursor-pointer"
                  >
                    <span className="font-bold text-navy text-sm sm:text-base">
                      {faq.q}
                    </span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 ${isOpen ? 'bg-clinical text-white rotate-180' : 'bg-gray-100 text-gray-500'
                      }`}>
                      <ChevronDown size={18} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-6 sm:px-6 sm:pb-6 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default Contact;
